// hooks/useSimConnection.js
'use client'

import { useEffect, useRef, useState, useCallback } from 'react'
import useGameStore from '../stores/gameStore'

// Resolve the sim-server WS URL. Production deploys (Vercel) need to
// point at a real host (a Cloudflare tunnel, ngrok, dedicated VPS, etc.)
// — set NEXT_PUBLIC_SIM_WS_URL at build time. Local dev falls back to
// localhost.
const DEFAULT_WS_URL =
  (typeof process !== 'undefined' && process.env.NEXT_PUBLIC_SIM_WS_URL) ||
  'ws://localhost:3001'
const RECONNECT_DELAY_BASE = 2000
// Never give up. Render restarts the sim on every push, and an open tab
// that stopped trying after ten attempts (about four minutes of backoff)
// stayed black for good until somebody reloaded it. The backoff is capped
// instead, so a tab left open through a deploy is back within fifteen
// seconds of the sim, and one that cannot reach it asks four times a
// minute rather than sixty.
const MAX_RECONNECT_ATTEMPTS = Number.POSITIVE_INFINITY
const RECONNECT_DELAY_MAX = 15000

/**
 * Hook for managing WebSocket connection to the sim-server
 * @param {string} wsUrl - WebSocket URL (default: NEXT_PUBLIC_SIM_WS_URL or ws://localhost:3001)
 * @returns {object} Connection state and controls
 */
export function useSimConnection(wsUrl = DEFAULT_WS_URL, { onDelta = null } = {}) {
  const wsRef = useRef(null)
  const reconnectTimeoutRef = useRef(null)
  const reconnectAttemptsRef = useRef(0)
  const connectRef = useRef(null)
  // Deltas arrive at 10Hz. Handing each one to React state re-rendered
  // the caller (the NPC bridge, inside the canvas) ten times a second for
  // the sake of an effect that only wrote into refs. A caller that passes
  // onDelta gets the message directly and no state is set; the ref keeps
  // the socket's handler stable across renders.
  const onDeltaRef = useRef(onDelta)
  useEffect(() => { onDeltaRef.current = onDelta }, [onDelta])

  const [connectionState, setConnectionState] = useState('disconnected')
  const [worldSnapshot, setWorldSnapshot] = useState(null)
  const [lastDelta, setLastDelta] = useState(null)
  const [error, setError] = useState(null)

  // Store actions for events, NPC states, entity states, and environment
  const addEvent = useGameStore((s) => s.addEvent)
  const setNpcStates = useGameStore((s) => s.setNpcStates)
  const setEntityStates = useGameStore((s) => s.setEntityStates)
  const mergeEntityStates = useGameStore((s) => s.mergeEntityStates)
  const setEnvironment = useGameStore((s) => s.setEnvironment)
  const startMemoryConsolidation = useGameStore((s) => s.startMemoryConsolidation)
  const endMemoryConsolidation = useGameStore((s) => s.endMemoryConsolidation)
  const setBrowseState = useGameStore((s) => s.setBrowseState)
  const clearBrowseState = useGameStore((s) => s.clearBrowseState)
  const setTunables = useGameStore((s) => s.setTunables)
  const patchTunable = useGameStore((s) => s.patchTunable)
  const setLastAdminResult = useGameStore((s) => s.setLastAdminResult)

  const connect = useCallback(() => {
    // Don't connect if already connected or connecting
    if (wsRef.current?.readyState === WebSocket.OPEN ||
        wsRef.current?.readyState === WebSocket.CONNECTING) {
      return
    }

    setConnectionState('connecting')
    setError(null)

    try {
      const ws = new WebSocket(wsUrl)
      wsRef.current = ws

      ws.onopen = () => {
        console.log('[SimConnection] Connected to sim-server')
        setConnectionState('connected')
        reconnectAttemptsRef.current = 0

        // Send client_hello
        ws.send(JSON.stringify({
          type: 'client_hello',
          timestamp: Date.now()
        }))
      }

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data)

          switch (message.type) {
            case 'world_snapshot':
              console.log(`[SimConnection] Snapshot: ${message.npcs?.length} NPCs`)
              setWorldSnapshot(message)
              // the town's clock, for what runs on it (the monorail's lift)
              if (message.serverTime) useGameStore.getState().setServerClockOffset(Date.parse(message.serverTime) - Date.now())
              // Update NPC states in store
              if (message.npcs) {
                setNpcStates(message.npcs)
              }
              // Update entity states in store
              if (message.entities) {
                setEntityStates(message.entities)
              }
              // Seed the live environment state from the snapshot
              if (message.world?.environment) {
                setEnvironment(message.world.environment)
              }

              // Tell server we're ready for deltas
              if (ws.readyState === WebSocket.OPEN) {
                ws.send(JSON.stringify({ type: 'client_ready' }))
              }
              break

            case 'world_delta':
              if (onDeltaRef.current) onDeltaRef.current(message)
              else setLastDelta(message)
              // Update NPC states with delta (skip empty — delta may carry only world state)
              if (message.npcs && message.npcs.length > 0) {
                setNpcStates(message.npcs)
              }
              // The whole entity list every half minute, and what changed in
              // between (sim-server EntityDelta.js) — keeps admin panels live
              if (message.entities) {
                setEntityStates(message.entities)
              }
              if (message.entitiesChanged || message.entitiesGone) {
                mergeEntityStates(message.entitiesChanged, message.entitiesGone)
              }
              // Periodic environment snapshot — update separate store slot,
              // NEVER mutate worldSnapshot (its reference is watched by
              // ServerNPCBridge's slot-allocation effect).
              if (message.environment) {
                setEnvironment(message.environment)
              }
              break

            case 'player_signal':
              // A walker's whistle/mark, echoed to every viewer. Dedupes the
              // sender's own optimistic render by id.
              if (message.kind) {
                useGameStore.getState().addSignal({ id: message.id, kind: message.kind, pos: message.pos })
              }
              break

            case 'event_log':
              // Add event to the ticker feed
              if (message.event) {
                addEvent(message.event)
              }
              break

            case 'browse_state':
              // Live internet browse session updates from runBrowseSession.
              //
              // This is a dumb pipe on purpose. It used to clear the state
              // itself 3.5s after 'end' OR 'failed', which is why the window
              // vanished a moment after opening: a failed page is common, and
              // that timer fired regardless of whether he was still sat there
              // browsing. Two components deciding when to close one window
              // meant the shorter timer always won.
              //
              // BrowsePopup owns the lifetime now, because it is the only
              // one that knows whether he has stopped browsing.
              setBrowseState(message)
              break

            case 'disk_state':
              // A floppy disk at the cafe, one step at a time (sim world/disks/Screen.js). Folded in the store.
              useGameStore.getState().pushDiskState(message)
              break

            case 'admin_result':
              // Server's response to an admin command. Mirrors authoritative
              // state into gameStore so panels can stop guessing.
              //   get_tunables → full tunables object
              //   set_tunable  → single { path, value } patch
              // Anything else is just surfaced as lastAdminResult so panels
              // can render success/failure toasts later if they want.
              setLastAdminResult(message)
              if (message.ok && message.command === 'get_tunables' && message.tunables) {
                setTunables(message.tunables)
              } else if (message.ok && message.command === 'set_tunable' && message.path != null) {
                patchTunable(message.path, message.value)
              }
              break

            case 'cognition_stage':
              // Sim-server signalling cognition pipeline state to the HUD.
              // Currently only 'consolidating' (during SLEEP wake-up) and
              // 'idle' (consolidation complete) are emitted.
              // Only the consolidation pair is acted on. Other stages used
              // to be mirrored into a store slice for a pipeline HUD that
              // was deleted; the slice went with it in the 31 Aug audit.
              if (message.stage === 'consolidating') {
                startMemoryConsolidation(message.durationMs || 8000, message.npcId || null)
              } else if (message.stage === 'idle') {
                endMemoryConsolidation()
              }
              break

            default:
              // Ignore unknown messages
              break
          }
        } catch (err) {
          console.error('[SimConnection] Failed to parse message:', err)
        }
      }

      ws.onerror = () => {
        // Errors during cleanup are expected in React Strict Mode
        setError('WebSocket connection error')
      }

      ws.onclose = (event) => {
        // Only the socket that currently owns the ref gets to declare the
        // connection dead. A stale socket's close (StrictMode's first
        // mount, or an old connection dying late) used to null the ref out
        // from under its replacement, orphaning a perfectly live socket
        // and leaving the page stuck on "disconnected".
        if (wsRef.current !== ws) return
        setConnectionState('disconnected')
        wsRef.current = null

        // Attempt reconnection with exponential backoff
        if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
          reconnectAttemptsRef.current++
          const delay = Math.min(RECONNECT_DELAY_MAX, RECONNECT_DELAY_BASE * Math.pow(1.5, reconnectAttemptsRef.current - 1))
          console.log(`[SimConnection] Reconnecting in ${Math.round(delay)}ms...`)
          reconnectTimeoutRef.current = setTimeout(() => connectRef.current?.(), delay)
        } else {
          setError('Max reconnection attempts reached')
          console.error('[SimConnection] Max reconnection attempts reached')
        }
      }
    } catch (err) {
      console.error('[SimConnection] Failed to create WebSocket:', err)
      setError(err.message)
      setConnectionState('disconnected')
    }
  }, [
    wsUrl,
    addEvent,
    endMemoryConsolidation,
    patchTunable,
    setBrowseState,
    setEntityStates,
    mergeEntityStates,
    setEnvironment,
    setLastAdminResult,
    setNpcStates,
    setTunables,
    startMemoryConsolidation,
  ])

  useEffect(() => { connectRef.current = connect }, [connect])

  const disconnect = useCallback(() => {
    // Clear any pending reconnection
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }

    // Close the socket, deaf: a deliberate disconnect must not let the
    // dying socket's onclose schedule a reconnect behind our back.
    if (wsRef.current) {
      const ws = wsRef.current
      wsRef.current = null
      ws.onclose = null
      ws.onerror = null
      ws.onmessage = null
      ws.close()
    }

    setConnectionState('disconnected')
    setError(null)
    // Reset reconnection attempts so future connects work
    reconnectAttemptsRef.current = 0
  }, [])

  const sendMessage = useCallback((message) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message))
      return true
    }
    return false
  }, [])

  const sendAdminCommand = useCallback((command, params = {}) => {
    if (wsRef.current?.readyState !== WebSocket.OPEN) return false
    // Read the admin token from sessionStorage on every send so a user
    // who pastes a token after the page is already open doesn't need to
    // refresh. Set via the admin "Auth" panel, or via:
    //   sessionStorage.setItem('threeEyesAdminToken', '<token>')
    let token = ''
    try { token = window.sessionStorage?.getItem('threeEyesAdminToken') || '' } catch {}
    wsRef.current.send(JSON.stringify({
      type: 'admin_command',
      command,
      params,
      token,
    }))
    return true
  }, [])

  const resetReconnection = useCallback(() => {
    reconnectAttemptsRef.current = 0
  }, [])

  // Cleanup on unmount — clear all pending timers + close socket so a
  // late-firing browse-state clear can't blank a fresh post-remount session.
  useEffect(() => {
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
        reconnectTimeoutRef.current = null
      }
      if (wsRef.current) {
        wsRef.current.close()
      }
    }
  }, [])

  return {
    connectionState,
    worldSnapshot,
    lastDelta,
    error,
    connect,
    disconnect,
    sendMessage,
    sendAdminCommand,
    resetReconnection,
    isConnected: connectionState === 'connected',
  }
}
